/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Grid, Link, Typography2, breakpoints, typography2, useMediaQuery } from '@coursera/cds-core';
import { SearchIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import SeoRelatedSearches from 'bundles/search-common/components/SeoRelatedSearches';
import { ON_SITE_SEARCH_PATH } from 'bundles/search-common/constants';
import type { HighlightItem, SearchIndexHitLearningProduct } from 'bundles/search-common/types';
import { getShowNewSERPSuggestedSearch as isNewSERPSuggestedSearch } from 'bundles/search-common/utils/experimentUtils';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';
import { shouldShowSuggestedSearches } from 'bundles/search-common/utils/utils';
import { useSeoPageData } from 'bundles/seo/contexts/SeoPageDataContext';

import _t from 'i18n!nls/search-common';

const useStyle = () => {
  return {
    container: css`
      padding-left: 24px;
      padding-bottom: 54px;
    `,
    mobileContainer: css`
      padding-left: 10px;
      padding-right: 10px;
    `,
    highlightedText: css`
      ${typography2.subtitleMedium};
      color: var(--cds-color-blue-600);
      background-color: var(--cds-color-white-0);
      font-style: normal;
    `,
    heading: css`
      padding: 24px 0;
    `,
  };
};

const useNewSuggestedSearchStyles = () => {
  const isMobile = useMediaQuery(breakpoints.down('xs'));
  const gridMargin = !isMobile ? '6px' : 0;
  return {
    suggestedSearchContainer: css`
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
    `,
    // using important here, because cds Link component overrides visited links color to a default cds color
    searchItemLink: css`
      ${typography2.actionSecondary}
      color: var(--cds-color-grey-975) !important;
      background-color: var(--cds-color-white-0);
      border: 1px solid var(--cds-color-grey-300);
      border-radius: 25px;
      padding: 6px 12px;
      display: flex;
      align-items: center;
      gap: 6px;
    `,
    highlightedText: css`
      font-style: normal;
      font-weight: bold;
    `,
    heading: css`
      padding-top: 24px;
      padding-bottom: 32px;
    `,
    serpFiltersBackgroundStyles: css`
      background-color: #f0f6ff;
      border-radius: var(--cds-spacing-200);
      margin-bottom: 24px;
      padding-bottom: 32px;
      padding-left: 20px;
      padding-right: 20px;
      margin-left: ${gridMargin};
      margin-right: ${gridMargin};
    `,
    gridContainerStyles: css`
      margin: auto;
      max-width: ${breakpoints.values.md}px;
    `,
  };
};

type EventingData = {
  searchIndex: string;
  searchIndexPosition: number;
};
type Props = { query?: string; hits?: SearchIndexHitLearningProduct[]; eventingData: EventingData };

export const SuggestedSearchesSection = ({
  suggestedSearchHits,
  eventingData,
}: {
  suggestedSearchHits: SearchIndexHitLearningProduct[];
  eventingData: EventingData;
}) => {
  const styles = useStyle();
  const track = useTracker();
  const isEqp = useIsEqp();
  const newSuggestedSearchStyles = useNewSuggestedSearchStyles();

  const onClick = (hit: SearchIndexHitLearningProduct) => {
    if (!isEqp && typeof window !== 'undefined') {
      window.scroll(0, 0);
    }
    track('perform_search', { searchedQuery: hit.name as string, sourceLinkCategory: 'eqp_search_suggestions' });
  };

  const NewSuggestedSearchesComponent = () => {
    return (
      <div css={newSuggestedSearchStyles.suggestedSearchContainer}>
        {suggestedSearchHits.map((hit) => {
          if (!hit.highlightResult) return null;

          return (
            <Link
              key={hit.name}
              variant="quiet"
              rel="nofollow"
              css={newSuggestedSearchStyles.searchItemLink}
              href={`${ON_SITE_SEARCH_PATH}?query=${hit.name}`}
              component={TrackedLink2}
              trackingName="search_page_suggested_search"
              onClick={() => onClick(hit)}
              data={{
                indexName: eventingData.searchIndex,
                indexPosition: eventingData.searchIndexPosition,
              }}
            >
              <span>
                {hit.highlightResult.map((item: HighlightItem, index: number) => {
                  return item.isHighlighted ? (
                    // eslint-disable-next-line react/no-array-index-key
                    <em css={newSuggestedSearchStyles.highlightedText} key={index}>
                      {item.value}
                    </em>
                  ) : (
                    // eslint-disable-next-line react/no-array-index-key
                    <span key={index}>{item.value}</span>
                  );
                })}
              </span>
              <SearchIcon size="medium" />
            </Link>
          );
        })}
      </div>
    );
  };

  const SuggestedSearchesComponent = () => {
    return (
      <Grid item container xs={12} spacing={12}>
        {suggestedSearchHits.map((hit) => {
          if (!hit.highlightResult) return null;

          return (
            <Grid item xs={12} sm={6} key={hit.name}>
              <Link
                variant="quiet"
                rel="nofollow"
                href={`${ON_SITE_SEARCH_PATH}?query=${hit.name}`}
                typographyVariant="subtitleMedium"
                component={TrackedLink2}
                trackingName="search_page_suggested_search"
                onClick={() => onClick(hit)}
                data={{
                  indexName: eventingData.searchIndex,
                  indexPosition: eventingData.searchIndexPosition,
                }}
              >
                {hit.highlightResult.map((item: HighlightItem, index: number) => {
                  return item.isHighlighted ? (
                    // eslint-disable-next-line react/no-array-index-key
                    <em css={styles.highlightedText} key={index}>
                      {item.value}
                    </em>
                  ) : (
                    // eslint-disable-next-line react/no-array-index-key
                    <span key={index}>{item.value}</span>
                  );
                })}
              </Link>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const shouldShowNewSERPSuggestedSearch = isNewSERPSuggestedSearch();

  return shouldShowNewSERPSuggestedSearch ? <NewSuggestedSearchesComponent /> : <SuggestedSearchesComponent />;
};

export const SuggestedSearches = ({ query, hits, eventingData }: Props) => {
  const styles = useStyle();
  const isMobile = useMediaQuery(breakpoints.down('xs'));
  const shouldShowNewSuggestedSearch = isNewSERPSuggestedSearch();
  const newSuggestedSearchStyles = useNewSuggestedSearchStyles();

  const { eqpData } = useSeoPageData();
  const seoRelatedSearches = eqpData?.headers?.relatedSearchHeader;

  if (seoRelatedSearches) {
    return <SeoRelatedSearches header={seoRelatedSearches.label} links={seoRelatedSearches.links} />;
  }

  const suggestedSearchesHits = hits?.filter((hit: SearchIndexHitLearningProduct) => hit.name !== query)?.slice(0, 8);

  if (!suggestedSearchesHits?.length || !shouldShowSuggestedSearches(query, hits)) return null;

  return (
    <TrackedDiv
      trackingName="search_page_suggested_search_section"
      trackClicks={false}
      withVisibilityTracking
      requireFullyVisible={false}
      data={{
        indexName: eventingData.searchIndex,
        indexPosition: eventingData.searchIndexPosition,
      }}
    >
      <div
        css={[
          styles.container,
          isMobile && shouldShowNewSuggestedSearch && styles.mobileContainer,
          shouldShowNewSuggestedSearch && newSuggestedSearchStyles.serpFiltersBackgroundStyles,
        ]}
      >
        <Grid
          css={[shouldShowNewSuggestedSearch && newSuggestedSearchStyles.gridContainerStyles]}
          container
          data-testid="suggested-searches"
        >
          <Grid item xs={12}>
            {shouldShowNewSuggestedSearch ? (
              <Typography2
                component="p"
                css={newSuggestedSearchStyles.heading}
                variant={isMobile ? 'titleSmall' : 'titleMedium'}
              >
                {_t('People also searched for')}
              </Typography2>
            ) : (
              <Typography2 css={styles.heading} variant="subtitleLarge" component="h2">
                {_t('Searches related to #{query}', { query })}
              </Typography2>
            )}
          </Grid>
          <SuggestedSearchesSection suggestedSearchHits={suggestedSearchesHits} eventingData={eventingData} />
        </Grid>
      </div>
    </TrackedDiv>
  );
};

export default SuggestedSearches;
